import React, { useEffect, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useDepartments } from "@/contexts/DepartmentsContext";
import ManageDepartmentsDialog from "@/components/ManageDepartmentsDialog";
import DepartmentRoster from "@/components/DepartmentRoster";
import DangerZoneCard from "@/components/DangerZoneCard";
import { X, UserPlus, Save, UserX, UserCheck, Layers, Trash2, Eye, EyeOff, Camera, ImagePlus, IdCard, Edit3, ArrowUpRight } from "lucide-react";
import { sortUsersByRole } from "@/lib/users";
import { resizeImage } from "@/lib/imageResize";
import PersonFormDialog from "@/components/PersonFormDialog";

const ROLES = [
  { val: "warehouse_manager", label: "Warehouse Manager" },
  { val: "operations_manager", label: "Operations Manager" },
  { val: "staff", label: "Staff" },
];

const COLORS = ["#228EBF", "#2C3E50", "#7A4DBA", "#B5651D", "#1E8E8E", "#C2185B", "#FFB300", "#001740"];

const DEPARTMENTS_FALLBACK = ["Production", "Milling", "3DP", "Reception"];

const empty = { name: "", pin: "", email: "", role: "staff", color: "#228EBF", departments: [], avatar_url: "" };

// Single-photo avatar upload widget for the user dialog. Uses the same
// take-photo / gallery split as MultiImageUpload so the WM can snap a
// headshot on their iPad and have it show up on the printable PO next
// to the primary-contact name. Photo is client-resized before upload
// so a phone camera shot doesn't blow up the avatar storage budget.
const AvatarUpload = ({ value, colour, name, onChange }) => {
  const cameraRef = React.useRef(null);
  const galleryRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const resized = await resizeImage(file, { maxSide: 720 });
      const fd = new FormData();
      fd.append("file", resized);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.path);
      toast.success("Photo uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div
        className="w-20 h-20 rounded-full border-2 border-black flex items-center justify-center font-display text-3xl overflow-hidden shrink-0"
        style={{ background: colour || "#228EBF", color: "#fff" }}
        data-testid="user-avatar-preview"
      >
        {value ? (
          <img
            src={imageUrl(value)}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          (name?.[0] || "?").toUpperCase()
        )}
      </div>
      <div className="flex flex-col gap-1.5 flex-1">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="user-avatar-take"
            onClick={() => cameraRef.current?.click()}
            disabled={busy}
            className="nb-btn bg-[#FFF7D6] px-3 py-2 flex items-center justify-center gap-1.5 text-xs font-bold"
          >
            <Camera size={14} strokeWidth={2.5} />
            {busy ? "…" : "Take photo"}
          </button>
          <button
            type="button"
            data-testid="user-avatar-gallery"
            onClick={() => galleryRef.current?.click()}
            disabled={busy}
            className="nb-btn bg-white px-3 py-2 flex items-center justify-center gap-1.5 text-xs font-bold"
          >
            <ImagePlus size={14} strokeWidth={2.5} />
            Gallery
          </button>
        </div>
        {value && (
          <button
            type="button"
            data-testid="user-avatar-remove"
            onClick={() => onChange("")}
            className="nb-btn bg-white px-3 py-1 text-[11px] flex items-center justify-center gap-1.5"
          >
            <X size={12} strokeWidth={2.5} /> Remove photo
          </button>
        )}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          data-testid="user-avatar-camera-input"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          hidden
          data-testid="user-avatar-gallery-input"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>
    </div>
  );
};

const Users = () => {
  const { names: deptNames } = useDepartments();
  const DEPARTMENTS = deptNames?.length ? deptNames : DEPARTMENTS_FALLBACK;
  const [users, setUsers] = useState([]);
  const [manageDeptsOpen, setManageDeptsOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  // v186 — People (non-login staff). Managed in the same Team dashboard
  // so a manager promoting a reception assistant is one click ("Promote
  // to login user…") without leaving the page.
  const [people, setPeople] = useState([]);
  const [personDlgOpen, setPersonDlgOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [showInactivePeople, setShowInactivePeople] = useState(false);
  const [promoteFor, setPromoteFor] = useState(null);
  const [promotePin, setPromotePin] = useState("");
  const [promoteRole, setPromoteRole] = useState("staff");
  const { user: me } = useAuth();

  const inactiveCount = users.filter((u) => !u.is_active).length;
  const visibleUsers = showInactive ? users : users.filter((u) => u.is_active);

  const purgeInactive = async () => {
    if (inactiveCount === 0) return toast.info("No inactive users to remove");
    if (
      !window.confirm(
        `Permanently delete ${inactiveCount} inactive user${inactiveCount === 1 ? "" : "s"}?\n\n` +
          `Historical stock movements will still show the original names — only the user records themselves are removed.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/users/bulk-purge");
      toast.success(`Removed ${data.deleted} user${data.deleted === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally {
      setBusy(false);
    }
  };

  const load = async () => {
    try {
      const [{ data: u }, { data: p }] = await Promise.all([
        api.get("/users"),
        api.get("/people?include_inactive=true").catch(() => ({ data: [] })),
      ]);
      setUsers(sortUsersByRole(u));
      setPeople(p || []);
    } catch {
      toast.error("Could not load team");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const startNewPerson = () => {
    setEditingPerson(null);
    setPersonDlgOpen(true);
  };
  const editPerson = (p) => {
    setEditingPerson(p);
    setPersonDlgOpen(true);
  };
  const togglePersonActive = async (p) => {
    try {
      if (p.active !== false) {
        await api.delete(`/people/${p.id}`);
        toast.success(`${p.name} deactivated`);
      } else {
        await api.put(`/people/${p.id}`, { active: true });
        toast.success(`${p.name} reactivated`);
      }
      load();
    } catch {
      toast.error("Action failed");
    }
  };
  const doPromote = async () => {
    if (!promoteFor) return;
    if (promotePin.length !== 6 || !/^\d+$/.test(promotePin)) {
      return toast.error("PIN must be 6 digits");
    }
    try {
      await api.post(`/people/${promoteFor.id}/promote-to-user`, {
        pin: promotePin,
        role: promoteRole,
      });
      toast.success(`${promoteFor.name} promoted to login user`);
      setPromoteFor(null);
      setPromotePin("");
      setPromoteRole("staff");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promotion failed");
    }
  };
  const visiblePeople = showInactivePeople
    ? people
    : people.filter((p) => p.active !== false);
  const inactivePersonCount = people.filter((p) => p.active === false).length;

  const startNew = () => {
    setForm(empty);
    setEditId(null);
    setOpen(true);
  };

  const startEdit = (u) => {
    setForm({
      name: u.name,
      pin: "",
      email: u.email || "",
      role: u.role,
      color: u.color,
      // Carry the saved avatar into the edit form so re-opening the
      // dialog shows the current photo instead of blank (regression
      // reported by user: photo persisted server-side + on the grid
      // but the dialog looked empty on re-edit, tempting the WM to
      // re-upload the same shot).
      avatar_url: u.avatar_url || "",
      departments: Array.isArray(u.departments)
        ? u.departments
        : u.department
        ? [u.department]
        : [],
    });
    setEditId(u.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    if (!editId && (form.pin.length !== 6 || !/^\d+$/.test(form.pin))) {
      return toast.error("PIN must be 6 digits");
    }
    if (editId && form.pin && (form.pin.length !== 6 || !/^\d+$/.test(form.pin))) {
      return toast.error("PIN must be 6 digits (or leave empty)");
    }
    const emailTrim = (form.email || "").trim();
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      return toast.error("Enter a valid email (or leave empty)");
    }
    setBusy(true);
    try {
      if (editId) {
        const payload = {
          name: form.name,
          role: form.role,
          color: form.color,
          email: emailTrim,
          departments: form.departments,
          avatar_url: form.avatar_url || "",
        };
        if (form.pin) payload.pin = form.pin;
        await api.put(`/users/${editId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/users", {
          ...form,
          email: emailTrim,
          departments: form.departments,
        });
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    if (u.id === me.id) return toast.error("Cannot deactivate yourself");
    try {
      if (u.is_active) {
        await api.delete(`/users/${u.id}`);
      } else {
        await api.put(`/users/${u.id}`, { is_active: true });
      }
      load();
    } catch {
      toast.error("Action failed");
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label-eyebrow">PIN-based access · shared iPad</div>
          <h1 className="font-display text-5xl leading-none mt-2">Team</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="manage-depts-open"
            onClick={() => setManageDeptsOpen(true)}
            className="nb-btn bg-white px-4 py-4 flex items-center gap-2"
          >
            <Layers size={18} strokeWidth={2.5} /> Manage departments
          </button>
          <button
            data-testid="user-toggle-inactive"
            onClick={() => setShowInactive((v) => !v)}
            className="nb-btn bg-white px-4 py-4 flex items-center gap-2"
            title={showInactive ? "Hide disabled/archived users" : "Show disabled/archived users"}
          >
            {showInactive ? (
              <>
                <EyeOff size={18} strokeWidth={2.5} /> Hide inactive
              </>
            ) : (
              <>
                <Eye size={18} strokeWidth={2.5} /> Show inactive
                {inactiveCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#607087] text-white text-[10px] font-bold">
                    {inactiveCount}
                  </span>
                )}
              </>
            )}
          </button>
          {showInactive && inactiveCount > 0 && (
            <button
              data-testid="user-purge-inactive"
              onClick={purgeInactive}
              disabled={busy}
              className="nb-btn nb-btn-danger px-4 py-4 flex items-center gap-2"
              title="Permanently delete all inactive users"
            >
              <Trash2 size={18} strokeWidth={2.5} /> Purge {inactiveCount}
            </button>
          )}
          <button
            data-testid="user-add-btn"
            onClick={startNew}
            className="nb-btn nb-btn-primary px-5 py-4 flex items-center gap-2"
          >
            <UserPlus size={20} strokeWidth={2.5} /> Add user
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleUsers.map((u) => (
          <div
            key={u.id}
            className="nb-card p-4 flex items-center gap-3"
            data-testid={`user-card-${u.id}`}
            style={{ opacity: u.is_active ? 1 : 0.55 }}
          >
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center border-2 border-black font-display text-2xl overflow-hidden"
              style={{ background: u.color || "#228EBF", color: "#fff" }}
              data-testid={`user-avatar-${u.id}`}
            >
              {u.avatar_url ? (
                <img
                  src={imageUrl(u.avatar_url)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                u.name?.[0]?.toUpperCase()
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg truncate">
                {u.name}
                {u.id === me.id && (
                  <span className="ml-2 label-eyebrow">(you)</span>
                )}
                {u.locked_at && (
                  <span
                    className="ml-2 inline-block text-[9px] font-bold tracking-widest uppercase text-white bg-[#E5263F] border-2 border-black rounded px-1.5 py-0.5"
                    data-testid={`user-locked-chip-${u.id}`}
                    title="3 wrong PINs — Edit user and set a new PIN to unlock"
                  >
                    Locked
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <div className="label-eyebrow px-1.5 py-0.5 rounded border-2 border-black inline-block bg-white">
                  {ROLES.find((r) => r.val === u.role)?.label || u.role}
                </div>
                {(() => {
                  const userDepts = Array.isArray(u.departments)
                    ? u.departments
                    : u.department
                    ? [u.department]
                    : [];
                  if (userDepts.length === 0) return null;
                  return userDepts.map((d) => (
                    <span
                      key={d}
                      className="text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-[#228EBF]/15 text-[#228EBF] border border-[#228EBF]/30"
                    >
                      {d}
                    </span>
                  ));
                })()}
              </div>
              {u.email && (
                <div
                  className="text-xs text-[#607087] truncate mt-1"
                  data-testid={`user-email-${u.id}`}
                  title={u.email}
                >
                  {u.email}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <button
                data-testid={`user-edit-${u.id}`}
                onClick={() => startEdit(u)}
                className="nb-btn bg-white px-3 py-1.5 text-xs"
              >
                Edit
              </button>
              <button
                data-testid={`user-toggle-${u.id}`}
                onClick={() => toggleActive(u)}
                disabled={u.id === me.id}
                className={`nb-btn px-3 py-1.5 text-xs flex items-center gap-1 ${
                  u.is_active ? "nb-btn-danger" : "nb-btn-primary"
                }`}
              >
                {u.is_active ? (
                  <UserX size={12} strokeWidth={2.5} />
                ) : (
                  <UserCheck size={12} strokeWidth={2.5} />
                )}
                {u.is_active ? "Disable" : "Enable"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* v186 — People (non-login staff) grid */}
      <div className="mt-10 pt-8 border-t-2 border-black/10" data-testid="people-section">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="label-eyebrow">Non-login staff · asset assignees only</div>
            <h2 className="font-display text-3xl leading-none mt-1 flex items-center gap-2">
              <IdCard size={26} strokeWidth={2.5} /> People
            </h2>
            <div className="text-xs text-[#607087] mt-1">
              Staff who need to appear as Asset owners but don't need iPad
              logins. Promote to a login user later if roles change.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="people-toggle-inactive"
              onClick={() => setShowInactivePeople((v) => !v)}
              className="nb-btn bg-white px-4 py-3 flex items-center gap-2 text-sm"
              title={showInactivePeople ? "Hide inactive people" : "Show inactive people"}
            >
              {showInactivePeople ? (
                <><EyeOff size={16} strokeWidth={2.5} /> Hide inactive</>
              ) : (
                <>
                  <Eye size={16} strokeWidth={2.5} /> Show inactive
                  {inactivePersonCount > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#607087] text-white text-[10px] font-bold">
                      {inactivePersonCount}
                    </span>
                  )}
                </>
              )}
            </button>
            <button
              data-testid="people-add-btn"
              onClick={startNewPerson}
              className="nb-btn nb-btn-primary px-5 py-3 flex items-center gap-2 text-sm"
            >
              <UserPlus size={16} strokeWidth={2.5} /> Add person
            </button>
          </div>
        </div>

        {visiblePeople.length === 0 ? (
          <div className="nb-card p-6 text-center text-sm text-[#607087]" data-testid="people-empty">
            No people yet. Add non-login staff so their laptops, monitors and
            desks can be tagged with a real name.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visiblePeople.map((p) => (
              <div
                key={p.id}
                className="nb-card p-4 flex items-start gap-3"
                data-testid={`person-card-${p.id}`}
                style={{ opacity: p.active === false ? 0.55 : 1 }}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center border-2 border-black font-display text-xl bg-[#607087] text-white shrink-0"
                >
                  {p.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg truncate">
                    {p.name}
                    {p.active === false && (
                      <span
                        className="ml-2 text-[9px] font-bold tracking-widest uppercase text-[#607087] border-2 border-[#607087] rounded px-1.5 py-0.5"
                        data-testid={`person-inactive-${p.id}`}
                      >
                        Inactive
                      </span>
                    )}
                    {p.promoted_user_id && (
                      <span
                        className="ml-2 text-[9px] font-bold tracking-widest uppercase text-[#107A3E] border-2 border-[#107A3E] rounded px-1.5 py-0.5"
                        title="Promoted to a login user"
                      >
                        Promoted
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    {p.role && (
                      <span className="label-eyebrow px-1.5 py-0.5 rounded border-2 border-black inline-block bg-white">
                        {p.role}
                      </span>
                    )}
                    {p.department && (
                      <span className="text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-[#228EBF]/15 text-[#228EBF] border border-[#228EBF]/30">
                        {p.department}
                      </span>
                    )}
                  </div>
                  {p.desk && (
                    <div className="text-xs text-[#607087] mt-1 truncate" title={p.desk}>
                      🪑 {p.desk}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button
                    data-testid={`person-edit-${p.id}`}
                    onClick={() => editPerson(p)}
                    className="nb-btn bg-white px-2 py-1.5 text-xs flex items-center gap-1"
                  >
                    <Edit3 size={12} strokeWidth={2.5} /> Edit
                  </button>
                  {!p.promoted_user_id && p.active !== false && (
                    <button
                      data-testid={`person-promote-${p.id}`}
                      onClick={() => {
                        setPromoteFor(p);
                        setPromotePin("");
                        setPromoteRole("staff");
                      }}
                      className="nb-btn bg-[#DFEEF7] border-[#0A3A7A] text-[#0A3A7A] px-2 py-1.5 text-xs flex items-center gap-1"
                      title="Turn into a login user (assign a PIN, keep their assets)"
                    >
                      <ArrowUpRight size={12} strokeWidth={2.5} /> Promote
                    </button>
                  )}
                  <button
                    data-testid={`person-toggle-${p.id}`}
                    onClick={() => togglePersonActive(p)}
                    className={`nb-btn px-2 py-1.5 text-xs flex items-center gap-1 ${
                      p.active === false ? "nb-btn-primary" : "nb-btn-danger"
                    }`}
                  >
                    {p.active === false ? (
                      <><UserCheck size={12} strokeWidth={2.5} /> Enable</>
                    ) : (
                      <><UserX size={12} strokeWidth={2.5} /> Disable</>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PersonFormDialog
        open={personDlgOpen}
        person={editingPerson}
        onClose={() => setPersonDlgOpen(false)}
        onSaved={load}
      />

      {promoteFor && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setPromoteFor(null)}
          data-testid="promote-overlay"
        >
          <div
            className="nb-card p-6 w-full max-w-sm bg-white"
            onClick={(e) => e.stopPropagation()}
            data-testid="promote-dialog"
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="label-eyebrow">Promote to login user</div>
                <h3 className="font-display text-xl mt-1">{promoteFor.name}</h3>
              </div>
              <button onClick={() => setPromoteFor(null)} className="nb-btn bg-white p-2">
                <X size={16} strokeWidth={2.5} />
              </button>
            </div>
            <div className="text-xs text-[#607087] mb-3">
              Creates a login user with this name{promoteFor.department ? ` in ${promoteFor.department}` : ""}. Any assets currently
              assigned to them keep the link and just switch to point at the
              new login user. This Person record is deactivated.
            </div>
            <div className="flex flex-col gap-3">
              <label>
                <div className="label-eyebrow mb-1">6-digit PIN</div>
                <input
                  data-testid="promote-pin-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="nb-input font-mono-num text-lg tracking-widest"
                  value={promotePin}
                  onChange={(e) => setPromotePin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="••••••"
                />
              </label>
              <label>
                <div className="label-eyebrow mb-1">Role</div>
                <select
                  data-testid="promote-role-select"
                  className="nb-input"
                  value={promoteRole}
                  onChange={(e) => setPromoteRole(e.target.value)}
                >
                  {ROLES.map((r) => (
                    <option key={r.val} value={r.val}>{r.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPromoteFor(null)} className="nb-btn bg-white px-4 py-2 text-sm">
                Cancel
              </button>
              <button
                data-testid="promote-confirm"
                onClick={doPromote}
                disabled={promotePin.length !== 6}
                className="nb-btn nb-btn-primary px-5 py-2 text-sm font-bold flex items-center gap-2"
              >
                <ArrowUpRight size={14} strokeWidth={2.5} /> Promote
              </button>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="user-form-overlay"
        >
          <div
            className="nb-card p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="user-form-dialog"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-display text-2xl">
                {editId ? "Edit user" : "New user"}
              </h3>
              <button onClick={() => setOpen(false)} className="nb-btn bg-white p-2">
                <X size={16} strokeWidth={2.5} />
              </button>
            </div>
            <div className="flex flex-col gap-3">
              <div>
                <div className="label-eyebrow mb-1">Profile photo</div>
                <AvatarUpload
                  value={form.avatar_url}
                  colour={form.color}
                  name={form.name}
                  onChange={(v) => setForm({ ...form, avatar_url: v })}
                />
                <div className="text-[11px] text-[#607087] mt-1.5">
                  Shows on the printable PO next to the primary Order
                  contact — helps the receiving supplier put a face to
                  the name.
                </div>
              </div>
              <label>
                <div className="label-eyebrow mb-1">Name</div>
                <input
                  data-testid="user-name-input"
                  className="nb-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </label>
              <label>
                <div className="label-eyebrow mb-1">
                  6-digit PIN {editId && "(leave empty to keep)"}
                </div>
                <input
                  data-testid="user-pin-input"
                  className="nb-input font-mono-num text-xl tracking-widest"
                  maxLength={6}
                  inputMode="numeric"
                  value={form.pin}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      pin: e.target.value.replace(/\D/g, "").slice(0, 6),
                    })
                  }
                  placeholder="••••••"
                />
              </label>
              <label>
                <div className="label-eyebrow mb-1">
                  Email <span className="text-[#607087]">(optional — used for PO forwarding)</span>
                </div>
                <input
                  data-testid="user-email-input"
                  type="email"
                  className="nb-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="alice@footwork.com.au"
                />
              </label>
              <label>
                <div className="label-eyebrow mb-1">Role</div>
                <select
                  data-testid="user-role-select"
                  className="nb-input"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                >
                  {ROLES.map((r) => (
                    <option key={r.val} value={r.val}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <div className="label-eyebrow mb-1">Departments (multi-select)</div>
                <div
                  data-testid="user-departments-chips"
                  className="flex flex-wrap gap-2 p-2 border-2 border-black rounded-xl bg-white"
                >
                  {DEPARTMENTS.map((d) => {
                    const active = form.departments.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        data-testid={`user-dept-chip-${d}`}
                        onClick={() =>
                          setForm({
                            ...form,
                            departments: active
                              ? form.departments.filter((x) => x !== d)
                              : [...form.departments, d],
                          })
                        }
                        className={`nb-btn px-3 py-1.5 text-sm transition-all ${
                          active ? "nb-btn-primary" : "bg-white"
                        }`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
                <div className="label-eyebrow mt-1 text-[#607087]">
                  Staff can only see / take / receive stock from their
                  selected departments. Items with no department are visible
                  to everyone. Managers see all.
                </div>
              </label>
              <div>
                <div className="label-eyebrow mb-1">Avatar colour</div>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      data-testid={`user-color-${c}`}
                      onClick={() => setForm({ ...form, color: c })}
                      className="w-9 h-9 rounded-full border-2 border-black"
                      style={{
                        background: c,
                        transform: form.color === c ? "scale(1.15)" : "scale(1)",
                        boxShadow: form.color === c ? "3px 3px 0 #001740" : "none",
                      }}
                    />
                  ))}
                </div>
              </div>
              <button
                data-testid="user-save-btn"
                onClick={save}
                disabled={busy}
                className="nb-btn nb-btn-primary py-3 text-lg mt-2 flex items-center justify-center gap-2"
              >
                <Save size={18} strokeWidth={2.5} /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      <ManageDepartmentsDialog
        open={manageDeptsOpen}
        onClose={() => setManageDeptsOpen(false)}
      />

      <DepartmentRoster users={users} />

      <DangerZoneCard />
    </div>
  );
};

export default Users;
